/**
 * ListingsIndexPage
 *
 * Table/list of the landlord's Listings with status filter, create CTA, and empty state.
 * Creating a Listing (and continuing a draft) happens in the guided setup, not here.
 * Route: /leasing/listings
 * US-1, US-3, US-4, US-5, US-6
 */

import { useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { LISTING_STATUS_BADGE_VARIANTS, LISTING_STATUS_LABELS } from '../../components/leasing/listing-status';
import { ListingPublishDialog } from '../../components/leasing/ListingPublishDialog';
import { RenewListingDialog } from '../../components/leasing/RenewListingDialog';
import { ListingPaidPeriodIndicator } from '../../components/leasing/ListingPaidPeriodIndicator';
import { MarketingStateChip } from '../../components/leasing/MarketingStateChip';
import { Skeleton } from '../../components/ui/skeleton';
import { MultiSelectFilter, type MultiSelectFilterOption } from '../../components/ui/multi-select-filter';
import {
  fetchListings,
  fetchUnits,
  LEASE_TERM_LABELS,
  type Listing,
  type ListingStatus,
  type UnitListItem,
} from '../../lib/leasing.api';
import { useMediaQuery } from '../../hooks/useMediaQuery';
import { formatDate, formatRentCents } from './listing-detail/shared';
import { formatUnitLabel } from '@resida/shared';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const STATUS_OPTIONS: { value: '' | ListingStatus; label: string }[] = [
  { value: '', label: 'All statuses' },
  { value: 'draft', label: 'Draft' },
  { value: 'active', label: 'Active' },
  { value: 'paused', label: 'Paused' },
  { value: 'closed', label: 'Closed' },
];

// Multi-select variant of STATUS_OPTIONS above, minus its `{ value: '', label: 'All
// statuses' }` sentinel — no longer meaningful once multi-select replaces the
// empty-string "all" convention.
const STATUS_OPTIONS_MULTI: MultiSelectFilterOption<ListingStatus>[] = STATUS_OPTIONS.filter(
  (opt): opt is MultiSelectFilterOption<ListingStatus> => opt.value !== ''
);

/** The guided setup's start page (unit picker). */
const NEW_LISTING_PATH = '/leasing/listings/new';
/** How long the publish/renew confirmation stays before it clears itself. */
const FEE_SUCCESS_AUTO_DISMISS_MS = 8000;

function unitLabel(unit: UnitListItem): string {
  return [formatUnitLabel(unit.unitNumber), unit.propertyName].filter(Boolean).join(' · ');
}

// ---------------------------------------------------------------------------
// Page Component
// ---------------------------------------------------------------------------

export function ListingsIndexPage() {
  const navigate = useNavigate();
  const isWide = useMediaQuery('(min-width: 768px)');
  const queryClient = useQueryClient();
  // AC-6: closed listings are excluded from the default view.
  const [statusFilter, setStatusFilter] = useState<ListingStatus[]>(['active', 'draft', 'paused']);
  const [page, setPage] = useState(1);
  // Renew / Republish straight from a row: which listing and which paid dialog.
  const [feeAction, setFeeAction] = useState<{ kind: 'publish' | 'renew'; listing: Listing } | null>(null);
  const [feeSuccessMessage, setFeeSuccessMessage] = useState<string | null>(null);

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['listings', { status: statusFilter, page }],
    queryFn: () => fetchListings({ status: statusFilter, page, pageSize: 20 }),
  });

  const { data: unitsData } = useQuery({
    queryKey: ['units'],
    queryFn: () => fetchUnits(),
  });

  const unitsMap = new Map<string, UnitListItem>(
    (unitsData?.units ?? []).map((u) => [u.id, u])
  );

  const getUnitDisplayName = (listing: Listing): string => {
    const unit = unitsMap.get(listing.unitId);
    if (unit) return unitLabel(unit);
    return listing.unitId.slice(0, 8) + '…';
  };

  const handleStatusChange = useCallback((next: ListingStatus[]) => {
    setStatusFilter(next);
    setPage(1);
  }, []);

  // The publish confirmation is a passing note, not a task: it clears itself.
  useEffect(() => {
    if (!feeSuccessMessage) return undefined;
    const timer = setTimeout(() => setFeeSuccessMessage(null), FEE_SUCCESS_AUTO_DISMISS_MS);
    return () => clearTimeout(timer);
  }, [feeSuccessMessage]);

  const handleFeeCompleted = useCallback(
    (message: string) => {
      setFeeAction(null);
      setFeeSuccessMessage(message);
      void queryClient.invalidateQueries({ queryKey: ['listings'] });
      void queryClient.invalidateQueries({ queryKey: ['listing'] });
    },
    [queryClient]
  );

  // Paid but not live: keep the dialog open, refresh so the Paid badge shows in the row behind it.
  const handlePaidNotPublished = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: ['listings'] });
    void queryClient.invalidateQueries({ queryKey: ['listing'] });
  }, [queryClient]);

  const feeUnit = feeAction ? unitsMap.get(feeAction.listing.unitId) : undefined;
  const feeUnitName = feeUnit ? (formatUnitLabel(feeUnit.unitNumber) ?? feeUnit.propertyName) : 'this unit';
  const feeUnitLabel = feeUnit ? unitLabel(feeUnit) : null;

  const items = data?.items ?? [];
  const totalCount = data?.totalCount ?? 0;
  const totalPages = Math.ceil(totalCount / 20);

  return (
    <div data-testid="listings-index-page" className="w-full max-w-[1600px] mx-auto pb-6 px-[var(--page-padding-x)] pt-6">
      {/* Toolbar */}
      <div className="mb-4 flex items-center gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <MultiSelectFilter
            testIdPrefix="listings-status"
            label="Status"
            options={STATUS_OPTIONS_MULTI}
            selected={statusFilter}
            onChange={handleStatusChange}
          />
        </div>
        <div className="ml-auto">
          <Button className="min-h-[44px]" data-testid="create-listing-button" onClick={() => navigate(NEW_LISTING_PATH)}>
            + Create Listing
          </Button>
        </div>
      </div>

      {feeSuccessMessage && (
        <div
          data-testid="listings-fee-success"
          role="status"
          className="mb-4 flex items-start justify-between gap-3 p-3 bg-success/10 border border-success/25 rounded-md text-success text-sm"
        >
          <span>{feeSuccessMessage}</span>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="min-h-[44px] -my-2"
            data-testid="listings-fee-success-dismiss"
            onClick={() => setFeeSuccessMessage(null)}
          >
            Dismiss
          </Button>
        </div>
      )}

      {error && (
        <div
          data-testid="listings-error"
          className="p-3 mb-4 bg-destructive/10 border border-destructive/30 rounded-md text-destructive text-sm flex items-center justify-between gap-3"
          role="alert"
        >
          <span>{error instanceof Error ? error.message : 'Failed to load listings'}</span>
          <Button variant="outline" size="sm" onClick={() => refetch()} data-testid="listings-retry">Retry</Button>
        </div>
      )}

      {isLoading && (
        <div data-testid="listings-loading" className="space-y-3">
          {[...Array(5)].map((_, i) => <Skeleton key={i} className="h-16 w-full" />)}
        </div>
      )}

      {!isLoading && !error && (
        <>
          {items.length === 0 ? (
            <div data-testid="listings-empty" className="py-16 text-center text-muted-foreground">
              {/* AC-8: an explicit empty-selection message when every status is
                  deselected, distinct from the generic "no results" copy. The
                  heading and CTA below also differentiate this case — creating a
                  listing wouldn't unblock a landlord who's deselected every status,
                  since the new listing would still be filtered out. */}
              <p className="text-base font-medium mb-1">
                {statusFilter.length === 0 ? 'No statuses selected' : 'No listings yet'}
              </p>
              <p className="text-sm mb-6" data-testid="listings-empty-message">
                {statusFilter.length === 0
                  ? 'Select at least one status to see listings.'
                  : statusFilter.length === STATUS_OPTIONS_MULTI.length
                    ? 'Create your first listing to get started.'
                    : 'No listings match the selected status filter.'}
              </p>
              {statusFilter.length > 0 && (
                <Button className="min-h-[44px]" data-testid="listings-empty-cta" onClick={() => navigate(NEW_LISTING_PATH)}>
                  + Create Listing
                </Button>
              )}
            </div>
          ) : (
            <div data-testid="listings-table-container" className="relative">
              {/* AC-L1 mobile fallback: at 375px the table still doesn't fit even at full
                  available width — overflow-x-auto lets it scroll within its own container
                  (matching LeasingLayout's subnav fade pattern) instead of being silently
                  clipped by AppShell's <main overflow-x-hidden>. */}
              <div
                aria-hidden="true"
                className="md:hidden pointer-events-none absolute right-0 top-0 bottom-0 w-8 bg-gradient-to-l from-background to-transparent z-10"
              />
              <div className="overflow-x-auto">
              <table className="w-full border-collapse text-sm" aria-label="Listings">
                <thead>
                  <tr className="border-b border-border">
                    <th scope="col" className="text-left py-3 px-3 font-medium text-muted-foreground">Unit / Listing</th>
                    <th scope="col" className="text-left py-3 px-3 font-medium text-muted-foreground">Status</th>
                    <th scope="col" className="text-left py-3 px-3 font-medium text-muted-foreground">Asking Rent</th>
                    {isWide && (
                      <th scope="col" className="text-left py-3 px-3 font-medium text-muted-foreground">Live period</th>
                    )}
                    <th scope="col" className="text-left py-3 px-3 font-medium text-muted-foreground">Available</th>
                    <th scope="col" className="text-left py-3 px-3 font-medium text-muted-foreground">Leads</th>
                    <th scope="col" className="py-3 px-3"><span className="sr-only">Actions</span></th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((listing) => {
                    const displayName = getUnitDisplayName(listing);
                    const periodIndicator = (l: Listing) => (
                      <ListingPaidPeriodIndicator
                        listing={l}
                        variant="index"
                        onRenew={() => setFeeAction({ kind: 'renew', listing: l })}
                        onRepublish={() => setFeeAction({ kind: 'publish', listing: l })}
                      />
                    );
                    const leaseTermsLabel = listing.acceptedLeaseTerms.map((t) => LEASE_TERM_LABELS[t] ?? t).join(', ');
                    return (
                      <tr
                        key={listing.id}
                        data-testid={`listing-row-${listing.id}`}
                        className="border-b border-border hover:bg-accent/50 cursor-pointer transition-colors"
                        onClick={() => navigate(`/leasing/listings/${listing.id}`)}
                      >
                        <td className="py-3 px-3">
                          <div className="font-medium text-foreground truncate max-w-[200px]" data-testid={`listing-unit-name-${listing.id}`}>
                            {displayName}
                          </div>
                          <div className="text-xs text-muted-foreground mt-0.5">{leaseTermsLabel}</div>
                          {/* Below md the Live period column would sit off-screen in the scrolling
                              table, so its state and Renew / Republish live under the unit. */}
                          {!isWide && (
                            <div className="mt-1.5" data-testid={`listing-live-period-${listing.id}`}>
                              {periodIndicator(listing)}
                            </div>
                          )}
                        </td>
                        <td className="py-3 px-3">
                          {/* Publication only; the Live period column carries Paid / Expired. */}
                          <div className="flex flex-wrap items-center gap-x-2">
                            <Badge
                              variant={LISTING_STATUS_BADGE_VARIANTS[listing.status]}
                              data-testid={`listing-status-badge-${listing.id}`}
                            >
                              {LISTING_STATUS_LABELS[listing.status]}
                            </Badge>
                            {/* listing-marketing-hub BR-4: Not marketed yet / N posts live (active only). */}
                            <MarketingStateChip
                              listingId={listing.id}
                              status={listing.status}
                              postedCount={listing.marketing?.postedCount ?? 0}
                              variant="index"
                            />
                          </div>
                        </td>
                        <td className="py-3 px-3 font-medium">
                          {formatRentCents(listing.askingRentCents)}
                          <span className="text-muted-foreground font-normal">/mo</span>
                        </td>
                        {isWide && (
                          <td className="py-3 px-3" data-testid={`listing-live-period-${listing.id}`}>
                            {periodIndicator(listing)}
                          </td>
                        )}
                        <td className="py-3 px-3 text-muted-foreground">{formatDate(listing.availableDate)}</td>
                        <td className="py-3 px-3 text-muted-foreground" data-testid={`listing-open-lead-count-${listing.id}`}>
                          <span>{listing.openLeadCount ?? '-'}</span>
                        </td>
                        <td className="py-3 px-3 text-right">
                          <Button
                            variant="outline"
                            size="sm"
                            className="min-h-[44px]"
                            data-testid={`listing-open-${listing.id}`}
                            onClick={(e) => { e.stopPropagation(); navigate(`/leasing/listings/${listing.id}`); }}
                          >
                            {listing.status === 'draft' ? 'Continue setup' : 'Open'}
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              </div>
            </div>
          )}

          {totalPages > 1 && (
            <div data-testid="listings-pagination" className="mt-6 flex items-center justify-between">
              <span className="text-sm text-muted-foreground">Page {page} of {totalPages} ({totalCount} total)</span>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage((p) => p - 1)} data-testid="listings-prev-page">Previous</Button>
                <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)} data-testid="listings-next-page">Next</Button>
              </div>
            </div>
          )}
        </>
      )}

      {feeAction && (
        feeAction.kind === 'publish' ? (
          <ListingPublishDialog
            open
            onOpenChange={(next) => { if (!next) setFeeAction(null); }}
            listing={feeAction.listing}
            unitName={feeUnitName}
            unitLabel={feeUnitLabel}
            onCompleted={handleFeeCompleted}
            onPaidNotPublished={handlePaidNotPublished}
          />
        ) : (
          <RenewListingDialog
            open
            onOpenChange={(next) => { if (!next) setFeeAction(null); }}
            listing={feeAction.listing}
            unitName={feeUnitName}
            unitLabel={feeUnitLabel}
            onCompleted={handleFeeCompleted}
          />
        )
      )}
    </div>
  );
}
